import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import type { HistoryEntry, FavoriteEntry, Settings, Video, WatchStats } from "@/types/video";
import { STORAGE_KEYS } from "@/types/video";

// History & favorites & settings — all stored in localStorage only,
// never uploaded (per spec #36 / #55).

interface PersistentState {
  history: HistoryEntry[];
  favorites: FavoriteEntry[];
  settings: Settings;
  lastMode: string;
  lastYear: number | null;
  lastCategory: string | null;
  blockedTags: string[];
  watchStats: WatchStats;
}

const defaultSettings: Settings = {
  muted: false,
  volume: 0.3,
  unknownMode: false,
  soundFx: true,
};

interface Actions {
  addHistory(entry: HistoryEntry): void;
  clearHistory(): void;
  toggleFavorite(entry: FavoriteEntry): boolean;
  isFavorite(bvid: string): boolean;
  removeFavorite(bvid: string): void;
  updateSettings(patch: Partial<Settings>): void;
  setLastFilter(mode: string, year: number | null, category: string | null): void;
  blockTag(tag: string): void;
  unblockTag(tag: string): void;
  recordVideoViewed(video: Video): void;
  recordWatchTime(milliseconds: number): void;
  hydrate(): void;
}

const HISTORY_LIMIT = 50;
const FAVORITES_LIMIT = 200;
const SEEN_VIDEOS_LIMIT = 2000;
const defaultWatchStats: WatchStats = {
  videoCount: 0,
  watchTimeMs: 0,
  tagCounts: {},
  seenBvids: {},
};

export const usePersistentStore = create<PersistentState & Actions>()(
  persist(
    (set, get) => ({
      history: [],
      favorites: [],
      settings: defaultSettings,
      lastMode: "random",
      lastYear: null,
      lastCategory: null,
      blockedTags: [],
      watchStats: defaultWatchStats,

      addHistory: (entry) => {
        const existing = get().history;
        // Move to top, dedupe
        const filtered = existing.filter((h) => h.bvid !== entry.bvid);
        const next = [entry, ...filtered].slice(0, HISTORY_LIMIT);
        set({ history: next });
      },

      clearHistory: () => set({ history: [] }),

      toggleFavorite: (entry) => {
        const favs = get().favorites;
        const exists = favs.some((f) => f.bvid === entry.bvid);
        if (exists) {
          set({ favorites: favs.filter((f) => f.bvid !== entry.bvid) });
          return false;
        }
        const next = [entry, ...favs].slice(0, FAVORITES_LIMIT);
        set({ favorites: next });
        return true;
      },

      isFavorite: (bvid) => get().favorites.some((f) => f.bvid === bvid),

      removeFavorite: (bvid) =>
        set({ favorites: get().favorites.filter((f) => f.bvid !== bvid) }),

      updateSettings: (patch) =>
        set({ settings: { ...get().settings, ...patch } }),

      setLastFilter: (mode, year, category) =>
        set({ lastMode: mode, lastYear: year, lastCategory: category }),

      blockTag: (tag) => {
        const normalized = tag.trim();
        if (!normalized || get().blockedTags.includes(normalized)) return;
        set({ blockedTags: [...get().blockedTags, normalized].sort() });
      },

      unblockTag: (tag) =>
        set({ blockedTags: get().blockedTags.filter((item) => item !== tag) }),

      recordVideoViewed: (video) => {
        const stats = get().watchStats;
        if (stats.seenBvids[video.bvid]) return;
        const seenBvids = { ...stats.seenBvids, [video.bvid]: true };
        const keys = Object.keys(seenBvids);
        if (keys.length > SEEN_VIDEOS_LIMIT) delete seenBvids[keys[0]];
        const tagCounts = { ...stats.tagCounts };
        video.tags.forEach((tag) => {
          tagCounts[tag] = (tagCounts[tag] ?? 0) + 1;
        });
        set({
          watchStats: {
            videoCount: Math.min(stats.videoCount + 1, SEEN_VIDEOS_LIMIT),
            watchTimeMs: stats.watchTimeMs,
            tagCounts,
            seenBvids,
          },
        });
      },

      recordWatchTime: (milliseconds) => {
        if (!Number.isFinite(milliseconds) || milliseconds <= 0) return;
        set({
          watchStats: {
            ...get().watchStats,
            watchTimeMs: get().watchStats.watchTimeMs + Math.round(milliseconds),
          },
        });
      },

      hydrate: () => {
        // no-op: persist middleware handles hydration
      },
    }),
    {
      name: "bilitime_state",
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        history: state.history,
        favorites: state.favorites,
        settings: state.settings,
        lastMode: state.lastMode,
        lastYear: state.lastYear,
        lastCategory: state.lastCategory,
        blockedTags: state.blockedTags,
        watchStats: state.watchStats,
      }),
    }
  )
);

// Single-purpose storage keys (per spec section #36).
// These are also exposed for components that prefer the raw API.
export const storage = {
  getHistory(): HistoryEntry[] {
    try {
      const raw = localStorage.getItem(STORAGE_KEYS.history);
      return raw ? (JSON.parse(raw) as HistoryEntry[]) : [];
    } catch {
      return [];
    }
  },
  setHistory(entries: HistoryEntry[]) {
    localStorage.setItem(STORAGE_KEYS.history, JSON.stringify(entries));
  },
  getFavorites(): FavoriteEntry[] {
    try {
      const raw = localStorage.getItem(STORAGE_KEYS.favorites);
      return raw ? (JSON.parse(raw) as FavoriteEntry[]) : [];
    } catch {
      return [];
    }
  },
  setFavorites(entries: FavoriteEntry[]) {
    localStorage.setItem(STORAGE_KEYS.favorites, JSON.stringify(entries));
  },
};
